/**
 * Configuration Tab for Demo Admin
 *
 * Contains three sections:
 * 1. Seed Configuration Data
 * 2. Generate Demo Data
 * 3. Purge Demo Data
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Chip
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import { SCOPE_TYPES, SEASONALITY_PROFILES, WEEKDAY_PROFILES } from '../constants';

/**
 * Configuration Tab Component
 * @param {Object} props - Component props
 * @param {boolean} props.configCheckLoading - Loading state for config check
 * @param {boolean|null} props.configExists - Whether config exists (null = not checked)
 * @param {Function} props.handleSeedConfig - Handler for seed config button
 * @param {boolean} props.seedLoading - Loading state for seed operation
 * @param {Object|null} props.seedMessage - Message object {type, text} for seed feedback
 * @param {string} props.genStartDate - Generation start date
 * @param {Function} props.setGenStartDate - Setter for generation start date
 * @param {string} props.genEndDate - Generation end date
 * @param {Function} props.setGenEndDate - Setter for generation end date
 * @param {string} props.genSeasonalityKey - Selected seasonality profile key
 * @param {Function} props.setGenSeasonalityKey - Setter for seasonality profile
 * @param {string} props.genWeekdayProfileKey - Selected weekday profile key
 * @param {Function} props.setGenWeekdayProfileKey - Setter for weekday profile
 * @param {number} props.genBaseSeed - Base seed for PRNG
 * @param {Function} props.setGenBaseSeed - Setter for base seed
 * @param {number} props.genNoiseSigma - Noise sigma value
 * @param {Function} props.setGenNoiseSigma - Setter for noise sigma
 * @param {Function} props.handleStartGeneration - Handler for start generation button
 * @param {boolean} props.genLoading - Loading state for generation operation
 * @param {Object|null} props.genMessage - Message object for generation feedback
 * @param {string} props.purgeScopeType - Selected purge scope type
 * @param {Function} props.setPurgeScopeType - Setter for purge scope type
 * @param {string} props.purgeScopeId - Purge scope ID
 * @param {Function} props.setPurgeScopeId - Setter for purge scope ID
 * @param {string} props.purgeStartDate - Purge start date
 * @param {Function} props.setPurgeStartDate - Setter for purge start date
 * @param {string} props.purgeEndDate - Purge end date
 * @param {Function} props.setPurgeEndDate - Setter for purge end date
 * @param {Function} props.handlePurgeData - Handler for purge data button
 * @param {boolean} props.purgeLoading - Loading state for purge operation
 * @param {Object|null} props.purgeMessage - Message object for purge feedback
 */
const ConfigurationTab = ({
  configCheckLoading,
  configExists,
  handleSeedConfig,
  seedLoading,
  seedMessage,
  genStartDate,
  setGenStartDate,
  genEndDate,
  setGenEndDate,
  genSeasonalityKey,
  setGenSeasonalityKey,
  genWeekdayProfileKey,
  setGenWeekdayProfileKey,
  genBaseSeed,
  setGenBaseSeed,
  genNoiseSigma,
  setGenNoiseSigma,
  handleStartGeneration,
  genLoading,
  genMessage,
  purgeScopeType,
  setPurgeScopeType,
  purgeScopeId,
  setPurgeScopeId,
  purgeStartDate,
  setPurgeStartDate,
  purgeEndDate,
  setPurgeEndDate,
  handlePurgeData,
  purgeLoading,
  purgeMessage,
}) => {
  return (
    <Box>
      {/* Config Seeding Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ color: '#7C3AED' }}>
            1. Seed Configuration Data
          </Typography>
          {configCheckLoading && (
            <Chip
              label="Checking..."
              size="small"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
          {!configCheckLoading && configExists === true && (
            <Chip
              label="✓ Already Configured"
              size="small"
              color="success"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
          {!configCheckLoading && configExists === false && (
            <Chip
              label="⚠ Not Configured"
              size="small"
              color="warning"
              sx={{ fontSize: '0.75rem' }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Initialize baseline configuration, seasonality profiles, and weekday patterns for selected client
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <AdminButton
            variant="contained"
            onClick={handleSeedConfig}
            disabled={seedLoading}
            sx={{
              height: '40px',
              minWidth: '160px'
            }}
          >
            {seedLoading ? <CircularProgress size={24} color="inherit" /> : 'Seed Config'}
          </AdminButton>
        </Box>

        {seedMessage && (
          <Alert severity={seedMessage.type} sx={{ mt: 2 }}>
            {seedMessage.text}
          </Alert>
        )}
      </Paper>

      {/* Data Generation Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#7C3AED' }}>
          2. Generate Demo Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generate atomic shift-daily demo data for selected client and specified date range
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            value={genStartDate}
            onChange={(e) => setGenStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
          />
          <TextField
            label="End Date"
            type="date"
            value={genEndDate}
            onChange={(e) => setGenEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
          />

          <FormControl sx={{ '& .MuiInputBase-root': { height: '40px' } }}>
            <InputLabel>Seasonality Profile</InputLabel>
            <Select
              value={genSeasonalityKey}
              onChange={(e) => setGenSeasonalityKey(e.target.value)}
              label="Seasonality Profile"
            >
              {SEASONALITY_PROFILES.map(profile => (
                <MenuItem key={profile.value} value={profile.value}>{profile.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ '& .MuiInputBase-root': { height: '40px' } }}>
            <InputLabel>Weekday Profile</InputLabel>
            <Select
              value={genWeekdayProfileKey}
              onChange={(e) => setGenWeekdayProfileKey(e.target.value)}
              label="Weekday Profile"
            >
              {WEEKDAY_PROFILES.map(profile => (
                <MenuItem key={profile.value} value={profile.value}>{profile.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Advanced Settings */}
        <Typography variant="subtitle2" sx={{ color: '#7C3AED', mt: 2, mb: 1 }}>
          Advanced: Override Baseline Settings
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
          <TextField
            label="Base Seed (Override)"
            type="number"
            value={genBaseSeed}
            onChange={(e) => setGenBaseSeed(e.target.value)}
            helperText="Default: 12345"
            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
          />
          <TextField
            label="Noise Sigma (Override)"
            type="number"
            value={genNoiseSigma}
            onChange={(e) => setGenNoiseSigma(e.target.value)}
            helperText="Default: 0.08 (8% variance)"
            inputProps={{ step: 0.01, min: 0, max: 0.5 }}
            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
          />
        </Box>

        {configExists === false && !configCheckLoading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Configuration must be seeded before generating data. Please complete Section 1 first.
          </Alert>
        )}

        <AdminButton
          variant="contained"
          onClick={handleStartGeneration}
          disabled={genLoading || configExists === false || configCheckLoading}
          fullWidth
          sx={{
            height: '40px'
          }}
        >
          {genLoading ? <CircularProgress size={24} color="inherit" /> : 'Start Generation'}
        </AdminButton>

        {genMessage && (
          <Alert severity={genMessage.type} sx={{ mt: 2 }}>
            {genMessage.text}
          </Alert>
        )}
      </Paper>

      {/* Purge Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#7C3AED' }}>
          3. Purge Demo Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Delete demo data for selected client, specified scope and date range
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
          <FormControl sx={{ '& .MuiInputBase-root': { height: '40px' } }}>
            <InputLabel>Scope Type</InputLabel>
            <Select
              value={purgeScopeType}
              onChange={(e) => setPurgeScopeType(e.target.value)}
              label="Scope Type"
            >
              {SCOPE_TYPES.map(type => (
                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {(purgeScopeType === 'ORGUNIT' || purgeScopeType === 'LOCATION') && (
            <TextField
              label="Scope ID"
              value={purgeScopeId}
              onChange={(e) => setPurgeScopeId(e.target.value)}
              placeholder="Enter Org Unit or Location ID"
              sx={{ gridColumn: 'span 2', '& .MuiInputBase-root': { height: '40px' } }}
            />
          )}

          <TextField
            label="Start Date"
            type="date"
            value={purgeStartDate}
            onChange={(e) => setPurgeStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
          />
          <TextField
            label="End Date"
            type="date"
            value={purgeEndDate}
            onChange={(e) => setPurgeEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiInputBase-root': { height: '40px' } }}
          />
        </Box>

        <AdminButton
          variant="contained"
          onClick={handlePurgeData}
          disabled={purgeLoading}
          fullWidth
          sx={{
            backgroundColor: '#EF5350',
            '&:hover': { backgroundColor: '#D32F2F' },
            '&:active': { backgroundColor: '#C62828' },
            height: '40px'
          }}
        >
          {purgeLoading ? <CircularProgress size={24} color="inherit" /> : 'Purge Demo Data'}
        </AdminButton>

        {purgeMessage && (
          <Alert severity={purgeMessage.type} sx={{ mt: 2 }}>
            {purgeMessage.text}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default ConfigurationTab;
